import { useState, useCallback, useMemo, useEffect } from "react";
import moment from "moment";
import {
	computeRelativeDateRange,
	DATE_PRESET_CUSTOM,
} from "utils/relativeDatePresets";

/**
 * Hook para gestionar los filtros de la vista de Attendance Supervisor
 * Centraliza toda la lógica de filtros y proporciona parámetros para consultas
 */
export default function useAttendanceFilters() {
	const DATE_PRESET_STORAGE_KEY = "attendance_datePreset";
	const STORAGE_START_KEY = "attendance_startDate";
	const STORAGE_END_KEY = "attendance_endDate";

	// Recuperar valores almacenados en localStorage o usar valores por defecto
	const getInitialDatePreset = () =>
		localStorage.getItem(DATE_PRESET_STORAGE_KEY) || DATE_PRESET_CUSTOM;

	const getInitialDateRange = () => {
		const storedPreset = getInitialDatePreset();

		// Si hay preset relativo guardado, recalcularlo siempre con "hoy"
		if (storedPreset && storedPreset !== DATE_PRESET_CUSTOM) {
			const range = computeRelativeDateRange(storedPreset, moment());
			if (range?.start && range?.end) return range;
		}

		const storedStartDate = localStorage.getItem(STORAGE_START_KEY);
		const storedEndDate = localStorage.getItem(STORAGE_END_KEY);

		return {
			start: storedStartDate
				? moment(storedStartDate)
				: moment().startOf("month"),
			end: storedEndDate ? moment(storedEndDate) : moment(),
		};
	};

	// Estados para filtros con valores iniciales desde localStorage
	const [dateRange, setDateRange] = useState(getInitialDateRange);
	const [activeDatePreset, setActiveDatePreset] = useState(getInitialDatePreset);
	const [employeeIds, setEmployeeIds] = useState(() => {
		const stored = localStorage.getItem("attendance_employeeIds") || "";
		// Limpiar datos antiguos que puedan tener corchetes
		const cleanStored = stored.replace(/[\[\]]/g, '').trim();
		if (cleanStored !== stored) {
			localStorage.setItem("attendance_employeeIds", cleanStored);
		}
		return cleanStored;
	});
	const [groupId, setGroupId] = useState(() => {
		return localStorage.getItem("attendance_groupId") || "";
	});
	const getInitialShowApproved = () => {
		const pendingNav = sessionStorage.getItem("attendance_initial_showApproved");
		if (pendingNav) {
			sessionStorage.removeItem("attendance_initial_showApproved");
			return pendingNav;
		}
		return "0";
	};

	const [showApproved, setShowApproved] = useState(getInitialShowApproved); // 0 = mostrar todos, 1 = mostrar sin aprobar
	const [showAlerts, setShowAlerts] = useState("-1"); // -1 = todos, 0 = sin alertas, 1 = con alertas
	const [notEnded, setNotEnded] = useState("0");
	const [showRestTime, setShowRestTime] = useState(false);

	// Parámetros para filtrado - serán pasados a las consultas de API
		const filterParams = useMemo(
		() => ({
			startDate: dateRange.start ? dateRange.start.format("YYYY-MM-DD") : "",
			endDate: dateRange.end ? dateRange.end.format("YYYY-MM-DD") : "",
			employeeIds: employeeIds || "",
			groupId: groupId || "",
			approved: showApproved,
			alerts: showAlerts,
			notEnded: notEnded,
			showRestTime: showRestTime,
		}),
		[
			dateRange.start,
			dateRange.end,
			employeeIds,
			groupId,
			showApproved,
			showAlerts,
			notEnded,
			showRestTime,
		]
	);

	// Funciones para actualizar filtros
	const handleDateChange = useCallback((dates, presetId = DATE_PRESET_CUSTOM) => {
		const { startDate, endDate } = dates;

		setActiveDatePreset(presetId);
		localStorage.setItem(DATE_PRESET_STORAGE_KEY, presetId);

		// Guardar en localStorage solo si las fechas son válidas
		if (startDate) {
			localStorage.setItem(
				STORAGE_START_KEY,
				startDate.format("YYYY-MM-DD")
			);
		} else {
			localStorage.removeItem(STORAGE_START_KEY);
		}

		if (endDate) {
			localStorage.setItem(STORAGE_END_KEY, endDate.format("YYYY-MM-DD"));
		} else {
			localStorage.removeItem(STORAGE_END_KEY);
		}

		setDateRange({
			start: startDate,
			end: endDate,
		});
	}, []);

	const setDatePreset = useCallback(
		(presetId) => {
			const nextPreset = presetId || DATE_PRESET_CUSTOM;
			setActiveDatePreset(nextPreset);
			localStorage.setItem(DATE_PRESET_STORAGE_KEY, nextPreset);

			// “Personalizada” no altera las fechas actuales
			if (nextPreset === DATE_PRESET_CUSTOM) return;

			const range = computeRelativeDateRange(nextPreset, moment());
			if (!range?.start || !range?.end) return;

			handleDateChange({ startDate: range.start, endDate: range.end }, nextPreset);
		},
		[handleDateChange]
	);

	const handleEmployeeChange = useCallback((ids) => {
		// Limpiar IDs de corchetes y espacios
		const cleanIds = ids ? ids.replace(/[\[\]]/g, '').trim() : '';
		console.log('Setting employee IDs:', cleanIds); // Debug
		localStorage.setItem("attendance_employeeIds", cleanIds);
		setEmployeeIds(cleanIds);
	}, []);

	const handleGroupChange = useCallback((id) => {
		localStorage.setItem("attendance_groupId", id || "");
		setGroupId(id);
	}, []);

	const toggleFilter = useCallback((filterId) => {
		switch (filterId) {
			case "filter_approved":
				setShowApproved((prev) => prev === "1" ? "0" : "1");
				break;
			case "not_ended":
				setNotEnded((prev) => prev === "1" ? "0" : "1");
				break;
			case "show_rest_time":
				setShowRestTime((prev) => !prev);
				break;
			default:
				break;
		}
	}, []);

	const setFilterAlerts = useCallback((value) => {
		setShowAlerts(value);
	}, []);

	return {
		// Estados
		dateRange,
		activeDatePreset,
		employeeIds,
		groupId,
		showApproved,
		showAlerts,
		notEnded,
		showRestTime,

		// Parámetros procesados para consultas
		filterParams,

		// Funciones para actualizar estados
		handleDateChange,
		setDatePreset,
		handleEmployeeChange,
		handleGroupChange,
		toggleFilter,
		setFilterAlerts,
	};
}
